import { useCallback } from "react";

import { useQueryClient } from "@tanstack/react-query";
import { useCurrentStateAndParams } from "@uirouter/react";

import { apiClient } from "app/modules/api-client/apiClient";

import type { GrowthOverviewGranularity } from "../const";
import { DOMAIN_PAGE_SIZE, GROWTH_OVERVIEW_CATEGORY_PAGE_SIZE, GROWTH_OVERVIEW_QUERY_KEY } from "../const";
import { useGrowthOverviewContext } from "../context/GrowthOverviewProvider";
import type { GrowthOverviewResponse, IndustriesBenchmarkGrowthOverviewListParams } from "../growthOverviewApiTypes";
import type { TGrowthOverviewRowType } from "../types";
import {
    buildGrowthOverviewQueryParams,
    getGrowthOverviewSortBy,
    normalizeGrowthOverviewSearch,
} from "../utils/buildGrowthOverviewQueryParams";

const buildBranchQueryParams = (
    granularity: GrowthOverviewGranularity,
    sortBy: ReturnType<typeof getGrowthOverviewSortBy>,
    search: string | undefined,
    category: string,
    rowType: TGrowthOverviewRowType,
    options: {
        domainOffset?: number;
        subIndustryOffset?: number;
    } = {},
): IndustriesBenchmarkGrowthOverviewListParams => {
    if (rowType === "industry") {
        return buildGrowthOverviewQueryParams({
            granularity,
            sortBy,
            search,
            categoryOverride: category,
            subIndustryOffset: options.subIndustryOffset,
            subIndustryLimit: GROWTH_OVERVIEW_CATEGORY_PAGE_SIZE,
        });
    }

    const domainOffset = options.domainOffset ?? 0;

    return buildGrowthOverviewQueryParams({
        granularity,
        sortBy,
        search,
        categoryOverride: category,
        domainOffset: domainOffset > 0 ? domainOffset : undefined,
        domainLimit: DOMAIN_PAGE_SIZE,
    });
};

export const useGrowthOverviewBranchData = () => {
    const queryClient = useQueryClient();
    const { granularity, metric, debouncedSearchTerm } = useGrowthOverviewContext();
    const { params } = useCurrentStateAndParams();
    const { category, country, duration, webSource, comparedDuration, includeSubdomains } = params;

    const fetchWithParams = useCallback(
        async (queryParams: IndustriesBenchmarkGrowthOverviewListParams): Promise<GrowthOverviewResponse> =>
            queryClient.fetchQuery({
                queryKey: [
                    GROWTH_OVERVIEW_QUERY_KEY,
                    "branch",
                    category,
                    country,
                    duration,
                    webSource,
                    comparedDuration,
                    includeSubdomains,
                    queryParams,
                ],
                queryFn: () => apiClient.IndustryAnalysis.industriesBenchmarkGrowthOverviewList(queryParams),
            }),
        [queryClient, category, country, duration, webSource, comparedDuration, includeSubdomains],
    );

    const fetchBranch = useCallback(
        async (
            branchCategory: string,
            rowType: TGrowthOverviewRowType,
            options: { domainOffset?: number; subIndustryOffset?: number } = {},
        ): Promise<GrowthOverviewResponse> => {
            const search = normalizeGrowthOverviewSearch(debouncedSearchTerm);
            const sortBy = getGrowthOverviewSortBy(metric);
            const queryParams = buildBranchQueryParams(granularity, sortBy, search, branchCategory, rowType, options);

            return fetchWithParams(queryParams);
        },
        [fetchWithParams, granularity, metric, debouncedSearchTerm],
    );

    const fetchIndustriesPage = useCallback(
        async (industryOffset: number, industryLimit: number): Promise<GrowthOverviewResponse> => {
            const search = normalizeGrowthOverviewSearch(debouncedSearchTerm);
            const sortBy = getGrowthOverviewSortBy(metric);
            const queryParams = buildGrowthOverviewQueryParams({
                granularity,
                sortBy,
                search,
                industryOffset,
                industryLimit,
            });

            return fetchWithParams(queryParams);
        },
        [fetchWithParams, granularity, metric, debouncedSearchTerm],
    );

    const fetchSubIndustriesPage = useCallback(
        async (subIndustryOffset: number, subIndustryLimit: number): Promise<GrowthOverviewResponse> => {
            const search = normalizeGrowthOverviewSearch(debouncedSearchTerm);
            const sortBy = getGrowthOverviewSortBy(metric);
            const queryParams = buildGrowthOverviewQueryParams({
                granularity,
                sortBy,
                search,
                subIndustryOffset,
                subIndustryLimit,
            });

            return fetchWithParams(queryParams);
        },
        [fetchWithParams, granularity, metric, debouncedSearchTerm],
    );

    return { fetchBranch, fetchIndustriesPage, fetchSubIndustriesPage };
};
